import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { reportsAPI } from '../lib/api';
import { fetchWithDedup } from '../lib/reportCache';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { ScheduleReportModal } from '../components/ScheduleReportModal';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip as ShadcnTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { FileText, Loader2, Download, Play, Filter, ChevronDown, ChevronRight, Settings2, History, Clock, MoreVertical, Printer, BarChart3, TableIcon, User, RotateCcw, Pin, PinOff, Trash2, RefreshCw, Archive, Save, BookTemplate, Edit, Bookmark, Info, AlertTriangle, X, CalendarClock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth, subMonths, formatDistanceToNow } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { ListPagination } from '../components/ListPagination';

// Chart colors
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// Format cell value based on type
const formatCellValue = (value, type) => {
  if (value === null || value === undefined) return '-';
  switch (type) {
    case 'currency':
      return `$${Number(value).toFixed(2)}`;
    case 'number':
      return Number(value).toLocaleString();
    case 'date':
      return value ? format(new Date(value), 'MMM d, yyyy') : '-';
    default:
      return String(value);
  }
};

// Get chart config based on report type
const getChartConfig = (reportId, columns, rows) => {
  if (!rows || rows.length === 0) return null;
  
  const labelCol = columns.find(c => c.type === 'string' || c.type === 'text') || columns[0];
  const valueCol = columns.find(c => c.type === 'currency' || c.type === 'number') || columns[1];
  
  if (!labelCol || !valueCol) return null;
  
  let chartTitle = 'Report Data';
  let valueLabel = valueCol.label;
  
  if (reportId?.includes('technician')) {
    chartTitle = 'By Technician';
  } else if (reportId?.includes('vehicle') || reportId?.includes('fleet')) {
    chartTitle = 'By Vehicle';
  } else if (reportId?.includes('aging')) {
    chartTitle = 'PO Aging';
  } else if (reportId?.includes('inventory')) {
    chartTitle = 'Inventory Value';
  }
  
  return {
    labelKey: labelCol.key,
    valueKey: valueCol.key,
    labelColumn: labelCol,
    valueColumn: valueCol,
    chartTitle,
    valueLabel
  };
};

// Define which filters are "advanced" (power user filters)
const ADVANCED_FILTER_IDS = ['actor_user_id', 'event_type', 'technician_id'];

// Skeleton for loading filter dropdowns
const FilterSkeleton = () => (
  <div className="h-10 bg-muted animate-pulse rounded-md" />
);

// Filter Controls Component
const FilterControls = ({
  reports, selectedReportId, setSelectedReportId, selectedReport,
  dateFrom, setDateFrom, dateTo, setDateTo, groupBy, setGroupBy,
  filters, setFilters, vendors, technicians, statuses, items, vehicles,
  actors, eventTypes, categories, locationTypes, approvalStatuses,
  approvalTypes, approvers, requesters, reportWarehouses, reportDepartments,
  running, handleRunReport, setDatePreset,
  onSaveTemplate, onSchedule, canSchedule, filtersLoading = {}, className = ''
}) => {
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  
  const basicFilters = selectedReport?.supported_filters?.filter(f => !ADVANCED_FILTER_IDS.includes(f.id)) || [];
  const advancedFilters = selectedReport?.supported_filters?.filter(f => ADVANCED_FILTER_IDS.includes(f.id)) || [];

  // Helper to check if a filter's data is loading
  const isFilterLoading = (filterId) => {
    const filterToKey = {
      vendor_id: 'vendors',
      technician_id: 'technicians',
      item_id: 'items',
      vehicle_id: 'vehicles',
      actor_user_id: 'actors',
      event_type: 'eventTypes',
      category_id: 'categories',
      location_type: 'locationTypes',
      status: 'approvalStatuses',
      receipt_status: 'statuses',
      request_type: 'approvalTypes',
      requester_user_id: 'requesters',
      decided_by_user_id: 'approvers',
      warehouse_id: 'reportWarehouses',
      department_id: 'reportDepartments',
    };
    return filtersLoading[filterToKey[filterId]] || false;
  };

  const renderFilter = (filter) => {
    // Show skeleton while filter data is loading
    if (isFilterLoading(filter.id)) {
      return (
        <div key={filter.id} className="space-y-1">
          <Label className="text-xs text-muted-foreground">{filter.name}</Label>
          <FilterSkeleton />
        </div>
      );
    }

    // Get options based on filter type
    const getFilterOptions = () => {
      switch (filter.id) {
        case 'vendor_id':
          return [{ value: 'all', label: 'All vendors' }, ...vendors.map((v) => ({ value: v.id, label: v.name }))];
        case 'technician_id':
          return [{ value: 'all', label: 'All technicians' }, ...technicians.map((t) => ({ value: t.id, label: `${t.name}${t.archived ? ' (archived)' : ''}` }))];
        case 'item_id':
          return [{ value: 'all', label: 'All items' }, ...items.map((i) => ({ value: i.id, label: `${i.name}${i.sku ? ` (${i.sku})` : ''}${i.archived ? ' (archived)' : ''}` }))];
        case 'vehicle_id':
          return [{ value: 'all', label: 'All vehicles' }, ...vehicles.map((v) => ({ value: v.id, label: `${v.name}${v.archived ? ' (archived)' : ''}` }))];
        case 'actor_user_id':
          return [{ value: 'all', label: 'All users' }, ...actors.map((a) => ({ value: a.id, label: `${a.name}${a.archived ? ' (archived)' : ''}` }))];
        case 'event_type':
          return [{ value: 'all', label: 'All event types' }, ...eventTypes.map((e) => ({ value: e.id, label: e.name }))];
        case 'category_id':
          return [{ value: 'all', label: 'All categories' }, ...categories.map((c) => ({ value: c.id, label: c.name }))];
        case 'location_type':
          return [{ value: 'all', label: 'All locations' }, ...locationTypes.map((l) => ({ value: l.id, label: l.name }))];
        case 'status':
          return [{ value: 'all', label: 'All statuses' }, ...approvalStatuses.map((s) => ({ value: s.id, label: s.name }))];
        case 'receipt_status':
          return [{ value: 'all', label: 'All statuses' }, ...statuses.map((s) => ({ value: s.id, label: s.name }))];
        case 'request_type':
          return [{ value: 'all', label: 'All types' }, ...approvalTypes.map((t) => ({ value: t.id, label: t.name }))];
        case 'requester_user_id':
          return [{ value: 'all', label: 'All requesters' }, ...requesters.map((r) => ({ value: r.id, label: `${r.name}${r.archived ? ' (archived)' : ''}` }))];
        case 'decided_by_user_id':
          return [{ value: 'all', label: 'All approvers' }, ...approvers.map((a) => ({ value: a.id, label: `${a.name}${a.archived ? ' (archived)' : ''}` }))];
        case 'warehouse_id':
          return [{ value: 'all', label: 'All warehouses' }, ...reportWarehouses.map((w) => ({ value: w.id, label: w.name }))];
        case 'department_id':
          return [{ value: 'all', label: 'All departments' }, ...reportDepartments.map((d) => ({ value: d.id, label: d.name }))];
        case 'po_source':
          return [
            { value: 'all', label: 'All POs' },
            { value: 'part_request', label: 'Only POs from Part Requests' },
          ];
        default:
          return [];
      }
    };

    const getPlaceholder = () => {
      const placeholders = {
        vendor_id: 'All vendors',
        technician_id: 'All technicians',
        item_id: 'All items',
        vehicle_id: 'All vehicles',
        actor_user_id: 'All users',
        event_type: 'All event types',
        category_id: 'All categories',
        location_type: 'All locations',
        status: 'All statuses',
        receipt_status: 'All statuses',
        request_type: 'All types',
        requester_user_id: 'All requesters',
        decided_by_user_id: 'All approvers',
        warehouse_id: 'All warehouses',
        department_id: 'All departments',
        po_source: 'All POs',
      };
      return placeholders[filter.id] || 'Select...';
    };

    const options = getFilterOptions();
    if (options.length === 0) return null;

    return (
      <div key={filter.id} className="space-y-1">
        <Label className="text-xs text-muted-foreground">{filter.name}</Label>
        <SearchableSelect
          options={options}
          value={filters[filter.id] || 'all'}
          onValueChange={(val) => setFilters({ ...filters, [filter.id]: val === 'all' ? undefined : val })}
          placeholder={getPlaceholder()}
          emptyText="No options found."
          className="h-10"
        />
      </div>
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="space-y-2">
        <Label className="text-sm font-bold uppercase tracking-wide">Report</Label>
        <SearchableSelect
          options={reports.map((report) => ({ value: report.id, label: report.name }))}
          value={selectedReportId}
          onValueChange={setSelectedReportId}
          placeholder="Select a report"
          emptyText="No reports found."
          className="h-12"
          data-testid="report-selector"
        />
        {selectedReport && <p className="text-xs text-muted-foreground">{selectedReport.description}</p>}
      </div>

      <div className="space-y-3">
        <Label className="text-sm font-bold uppercase tracking-wide">Date Range</Label>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setDatePreset('last7')}>Last 7 Days</Button>
          <Button variant="outline" size="sm" onClick={() => setDatePreset('lastMonth')}>Last Month</Button>
          <Button variant="outline" size="sm" onClick={() => setDatePreset('thisMonth')}>This Month</Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">From</Label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full h-10 px-3 border border-border rounded-md text-sm bg-background" data-testid="date-from" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">To</Label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full h-10 px-3 border border-border rounded-md text-sm bg-background" data-testid="date-to" />
          </div>
        </div>
      </div>

      {(basicFilters.length > 0 || selectedReport?.supported_group_by?.length > 0) && (
        <div className="space-y-3">
          <Label className="text-sm font-bold uppercase tracking-wide">Filters</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {selectedReport?.supported_group_by?.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Group By</Label>
                <Select value={groupBy || 'none'} onValueChange={(v) => setGroupBy(v === 'none' ? '' : v)}>
                  <SelectTrigger className="h-10" data-testid="group-by-selector"><SelectValue placeholder="No grouping" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No grouping</SelectItem>
                    {selectedReport.supported_group_by.map((gb) => (<SelectItem key={gb} value={gb}>{gb.charAt(0).toUpperCase() + gb.slice(1)}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {basicFilters.map(renderFilter)}
          </div>
        </div>
      )}

      {advancedFilters.length > 0 && (
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between px-2 h-9 text-muted-foreground hover:text-foreground">
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide">
                <Settings2 className="w-3.5 h-3.5" />Advanced Filters
              </span>
              {advancedOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {advancedFilters.map(renderFilter)}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <Button className="w-full sm:w-auto h-12 font-bold px-8" onClick={handleRunReport} disabled={running || !selectedReportId} data-testid="run-report-btn">
          {running ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running...</>) : (<><Play className="w-4 h-4 mr-2" />Run Report</>)}
        </Button>
        {onSaveTemplate && (
          <Button variant="outline" className="w-full sm:w-auto h-12" onClick={onSaveTemplate} disabled={!selectedReportId} data-testid="save-template-btn">
            <Save className="w-4 h-4 mr-2" />Save as Template
          </Button>
        )}
        {canSchedule && (
          <Button variant="outline" className="w-full sm:w-auto h-12" onClick={onSchedule} disabled={!selectedReportId} data-testid="schedule-report-btn">
            <CalendarClock className="w-4 h-4 mr-2" />Schedule
          </Button>
        )}
      </div>
    </div>
  );
};

export const Reports = () => {
  const { hasPerm, user, tenant } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReportId, setSelectedReportId] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(() => format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [groupBy, setGroupBy] = useState('');
  const [filters, setFilters] = useState({});
  
  // Filter options - lazy loaded
  const [statuses, setStatuses] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [actors, setActors] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [approvalStatuses, setApprovalStatuses] = useState([]);
  const [approvalTypes, setApprovalTypes] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [requesters, setRequesters] = useState([]);
  const [reportWarehouses, setReportWarehouses] = useState([]);
  const [reportDepartments, setReportDepartments] = useState([]);
  
  // Track which filter datasets have been loaded
  const loadedFilters = useRef(new Set());
  const [filtersLoading, setFiltersLoading] = useState({});
  
  // Dev timing (only in development)
  const mountTime = useRef(performance.now());
  const firstRenderLogged = useRef(false);
  
  // Results state
  const [result, setResult] = useState(null);
  // Drill-down state for Part Request → PO report
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [drillDownLabel, setDrillDownLabel] = useState('');
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [resultsTab, setResultsTab] = useState('table');
  const [resultRanBy, setResultRanBy] = useState(null);
  
  // Phase 3.5: URL-synced results pagination state
  const resultsPage = parseInt(searchParams.get('results_page')) || 1;
  const resultsPageSize = parseInt(searchParams.get('results_per_page')) || 25;
  const [currentReportRunId, setCurrentReportRunId] = useState(null);
  const [totalResultRows, setTotalResultRows] = useState(0);
  const [totalResultPages, setTotalResultPages] = useState(0);
  const [paginatedRows, setPaginatedRows] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  
  // Server-persisted history
  const [reportHistory, setReportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  
  // Phase 3.5: URL-synced history pagination state
  const historyPage = parseInt(searchParams.get('history_page')) || 1;
  const historyPageSize = parseInt(searchParams.get('history_per_page')) || 20;
  const [totalHistoryItems, setTotalHistoryItems] = useState(0);
  const [totalHistoryPages, setTotalHistoryPages] = useState(0);
  
  // Helper to update URL params
  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        // Clean up default values
        if (value === null || value === undefined || value === '' || 
            (key === 'results_page' && value === 1) || 
            (key === 'results_per_page' && value === 25) ||
            (key === 'history_page' && value === 1) ||
            (key === 'history_per_page' && value === 20)) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);
  
  // URL-synced setters
  const setResultsPage = useCallback((value) => {
    updateParams({ results_page: value });
  }, [updateParams]);
  
  const setResultsPageSize = useCallback((value) => {
    updateParams({ results_per_page: value, results_page: 1 });
  }, [updateParams]);
  
  const setHistoryPage = useCallback((value) => {
    updateParams({ history_page: value });
  }, [updateParams]);
  
  const setHistoryPageSize = useCallback((value) => {
    updateParams({ history_per_page: value, history_page: 1 });
  }, [updateParams]);
  
  // Clear history dialog
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  
  // Saved Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(true);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteTemplateConfirm, setDeleteTemplateConfirm] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [dateRangeType, setDateRangeType] = useState('last_month');
  
  // Phase 3.5: Fetch paginated results from server
  const fetchPaginatedResults = async (reportRunId, pageNum = 1, pageSizeVal = 25) => {
    if (!reportRunId) return;
    
    try {
      setResultsLoading(true);
      const response = await reportsAPI.getResults(reportRunId, {
        page: pageNum,
        page_size: pageSizeVal
      });
      const data = response.data;
      
      // Update result structure with paginated data
      setResult(prev => ({
        ...prev,
        columns: data.columns,
        rows: data.rows, // Only this page's rows
        totals: data.totals
      }));
      setPaginatedRows(data.rows);
      setTotalResultRows(data.total_rows);
      setTotalResultPages(data.total_pages);
      setResultsPage(data.page);
    } catch (error) {
      console.error('Failed to fetch paginated results:', error);
      toast.error('Failed to load results page');
    } finally {
      setResultsLoading(false);
    }
  };
  
  // Phase 3.5: Results pagination handlers (now server-side with URL sync)
  const handleResultsPageChange = async (newPage) => {
    setResultsPage(newPage);
    if (currentReportRunId) {
      await fetchPaginatedResults(currentReportRunId, newPage, resultsPageSize);
    }
  };
  
  const handleResultsPageSizeChange = async (newPageSize) => {
    setResultsPageSize(newPageSize);
    if (currentReportRunId) {
      await fetchPaginatedResults(currentReportRunId, 1, newPageSize);
    }
  };
  
  // Phase 3.5: History pagination handlers (server-side with URL sync)
  const handleHistoryPageChange = (newPage) => {
    setHistoryPage(newPage);
  };
  
  const handleHistoryPageSizeChange = (newPageSize) => {
    setHistoryPageSize(newPageSize);
  };

  // Load templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const response = await reportsAPI.getTemplates();
        setTemplates(response.data || []);
      } catch (error) {
        console.warn('Failed to load report templates:', error);
      } finally {
        setTemplatesLoading(false);
      }
    };
    loadTemplates();
  }, []);

  // Load server history with pagination (Phase 3.5)
  const loadHistory = async (pageNum = historyPage, pageSizeVal = historyPageSize) => {
    try {
      setHistoryLoading(true);
      const response = await reportsAPI.getHistory({ page: pageNum, page_size: pageSizeVal });
      const data = response.data;
      setReportHistory(data.items || []);
      setTotalHistoryItems(data.total || 0);
      setTotalHistoryPages(data.total_pages || 0);
    } catch (error) {
      console.warn('Failed to load report history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };
  
  useEffect(() => {
    loadHistory();
  }, []);
  
  // Phase 3.5: Reload history when page changes
  useEffect(() => {
    loadHistory(historyPage, historyPageSize);
  }, [historyPage, historyPageSize]);

  // === PHASE 1: Load only essential data on mount ===
  // Report definitions only - filter data is lazy-loaded
  useEffect(() => {
    const loadEssentialData = async () => {
      // Dev timing
      if (process.env.NODE_ENV === 'development') {
        console.log('[Reports] Mount started at', performance.now() - mountTime.current, 'ms');
      }
      
      try {
        // Only fetch report definitions - this is required to render the page
        const reportsRes = await reportsAPI.list();
        setReports(reportsRes.data || []);
        
        if (reportsRes.data?.length > 0) {
          setSelectedReportId(reportsRes.data[0].id);
        }
        
        // Dev timing
        if (process.env.NODE_ENV === 'development') {
          console.log('[Reports] Essential data loaded at', performance.now() - mountTime.current, 'ms');
        }
      } catch (error) {
        toast.error('Failed to load reports');
      } finally {
        setLoading(false);
        
        // Dev timing - first render ready
        if (process.env.NODE_ENV === 'development' && !firstRenderLogged.current) {
          firstRenderLogged.current = true;
          console.log('[Reports] First render ready at', performance.now() - mountTime.current, 'ms');
        }
      }
    };
    loadEssentialData();
  }, []);

  // === PHASE 2: Lazy-load filter data on demand ===
  // Map filter IDs to their fetch functions and setters
  const filterConfig = useMemo(() => ({
    vendor_id: { fetch: reportsAPI.getVendors, setter: setVendors, key: 'vendors' },
    technician_id: { fetch: reportsAPI.getTechnicians, setter: setTechnicians, key: 'technicians' },
    item_id: { fetch: reportsAPI.getItems, setter: setItems, key: 'items' },
    vehicle_id: { fetch: reportsAPI.getVehicles, setter: setVehicles, key: 'vehicles' },
    actor_user_id: { fetch: reportsAPI.getActors, setter: setActors, key: 'actors' },
    event_type: { fetch: reportsAPI.getEventTypes, setter: setEventTypes, key: 'eventTypes' },
    category_id: { fetch: reportsAPI.getCategories, setter: setCategories, key: 'categories' },
    location_type: { fetch: reportsAPI.getLocationTypes, setter: setLocationTypes, key: 'locationTypes' },
    status: { fetch: reportsAPI.getApprovalStatuses, setter: setApprovalStatuses, key: 'approvalStatuses' },
    receipt_status: { fetch: reportsAPI.getStatuses, setter: setStatuses, key: 'statuses' },
    request_type: { fetch: reportsAPI.getApprovalTypes, setter: setApprovalTypes, key: 'approvalTypes' },
    requester_user_id: { fetch: reportsAPI.getRequesters, setter: setRequesters, key: 'requesters' },
    decided_by_user_id: { fetch: reportsAPI.getApprovers, setter: setApprovers, key: 'approvers' },
    warehouse_id: { fetch: reportsAPI.getWarehouses, setter: setReportWarehouses, key: 'reportWarehouses' },
    department_id: { fetch: reportsAPI.getDepartments, setter: setReportDepartments, key: 'reportDepartments' },
  }), []);

  // Lazy load a specific filter dataset
  const loadFilterData = useCallback(async (filterId) => {
    const config = filterConfig[filterId];
    if (!config) return;
    
    const tenantId = tenant?.id || 'default';
    
    // Skip if already loaded or loading
    if (loadedFilters.current.has(config.key)) return;
    if (filtersLoading[config.key]) return;
    
    // Mark as loading
    setFiltersLoading(prev => ({ ...prev, [config.key]: true }));
    
    try {
      // Use cached fetch with deduplication
      const data = await fetchWithDedup(config.key, tenantId, config.fetch);
      config.setter(data || []);
      loadedFilters.current.add(config.key);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Reports] Loaded filter: ${config.key} at`, performance.now() - mountTime.current, 'ms');
      }
    } catch (error) {
      console.warn(`Failed to load ${config.key}:`, error);
    } finally {
      setFiltersLoading(prev => ({ ...prev, [config.key]: false }));
    }
  }, [filterConfig, tenant?.id, filtersLoading]);

  // Load filter data when report selection changes
  useEffect(() => {
    if (!selectedReport?.supported_filters) return;
    
    // Determine which filters this report needs
    const neededFilters = selectedReport.supported_filters.map(f => f.id);
    
    // Load all needed filter datasets in parallel
    const unloadedFilters = neededFilters.filter(fId => {
      const config = filterConfig[fId];
      return config && !loadedFilters.current.has(config.key);
    });
    
    if (unloadedFilters.length > 0) {
      // Load all unloaded filters in parallel
      Promise.all(unloadedFilters.map(fId => loadFilterData(fId)));
    }
  }, [selectedReport, filterConfig, loadFilterData]);

  // Update selected report when ID changes
  useEffect(() => {
    const report = reports.find(r => r.id === selectedReportId);
    setSelectedReport(report || null);
    // Restore po_source from URL when switching to the PO report
    const newFilters = {};
    if (selectedReportId === 'part_request_pos') {
      const urlPoSource = searchParams.get('po_source');
      if (urlPoSource === 'part_request') {
        newFilters.po_source = urlPoSource;
      }
    }
    setFilters(newFilters);
    setGroupBy('');
  }, [selectedReportId, reports]);

  // Sync po_source filter ↔ URL
  useEffect(() => {
    if (selectedReportId !== 'part_request_pos') return;
    const urlVal = searchParams.get('po_source');
    const filterVal = filters.po_source || null;
    if (filterVal !== urlVal) {
      updateParams({ po_source: filterVal });
    }
  }, [filters.po_source, selectedReportId]);

  // Run report and save to server history
  const handleRunReport = useCallback(async (historyEntryId = null, forceRefresh = false) => {
    if (!selectedReportId) {
      toast.error('Please select a report');
      return;
    }
    
    setRunning(true);
    try {
      const response = await reportsAPI.run({
        report_id: selectedReportId,
        date_from: dateFrom,
        date_to: dateTo,
        group_by: groupBy || null,
        filters: filters,
      });
      
      // Store full result for export, but display will be paginated
      const fullResult = response.data;
      setResult(fullResult);
      setResultRanBy(user?.name || '—');
      setResultsTab('table');
      
      // Phase 3.5: Store ALL rows in result_summary_json for server-side pagination
      const resultSummary = {
        columns: fullResult.columns,
        rows: fullResult.rows || [], // Store ALL rows, not truncated
        totals: fullResult.totals,
        generated_at: fullResult.generated_at,
      };
      
      // Save to server history
      const historyData = {
        report_key: selectedReportId,
        report_name: selectedReport?.name || selectedReportId,
        params_json: { date_from: dateFrom, date_to: dateTo, filters, group_by: groupBy },
        result_summary_json: resultSummary,
        row_count: fullResult.rows?.length || 0,
      };
      
      try {
        let reportRunId = null;
        if (historyEntryId && forceRefresh) {
          // Update existing entry (Run again)
          const updateRes = await reportsAPI.updateHistory(historyEntryId, historyData);
          setReportHistory(prev => prev.map(h => h.id === historyEntryId ? updateRes.data : h));
          reportRunId = historyEntryId;
          toast.success('Report refreshed');
        } else {
          // Create new entry
          const createRes = await reportsAPI.createHistory(historyData);
          setReportHistory(prev => [createRes.data, ...prev].slice(0, 20));
          reportRunId = createRes.data.id;
          toast.success(`${selectedReport?.name || 'Report'} generated`);
        }
        
        // Phase 3.5: Set the report run ID for server-side pagination
        setCurrentReportRunId(reportRunId);
        setTotalResultRows(fullResult.rows?.length || 0);
        setTotalResultPages(Math.ceil((fullResult.rows?.length || 0) / resultsPageSize) || 1);
        setResultsPage(1);
        
        // For initial display, show first page of results
        const firstPageRows = (fullResult.rows || []).slice(0, resultsPageSize);
        setPaginatedRows(firstPageRows);
      } catch (historyError) {
        console.warn('Failed to save report history:', historyError);
        // Fall back to client-side pagination if save fails
        setCurrentReportRunId(null);
        setTotalResultRows(fullResult.rows?.length || 0);
        setTotalResultPages(Math.ceil((fullResult.rows?.length || 0) / resultsPageSize) || 1);
        setResultsPage(1);
        setPaginatedRows((fullResult.rows || []).slice(0, resultsPageSize));
        toast.success(`${selectedReport?.name || 'Report'} generated`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run report');
    } finally {
      setRunning(false);
    }
  }, [selectedReportId, selectedReport, dateFrom, dateTo, groupBy, filters, user, resultsPageSize]);

  // Load saved results from history entry
  const handleLoadSaved = useCallback((entry) => {
    setSelectedReportId(entry.report_key);
    
    const params = entry.params_json || {};
    if (params.date_from) setDateFrom(params.date_from);
    if (params.date_to) setDateTo(params.date_to);
    if (params.filters) setFilters(params.filters);
    if (params.group_by !== undefined) setGroupBy(params.group_by || '');
    
    if (entry.result_summary_json) {
      // Phase 3.5: Set report run ID and use server-side pagination
      setCurrentReportRunId(entry.id);
      const savedResult = entry.result_summary_json;
      setResult(savedResult);
      setResultRanBy(entry.ran_by_label || '—');
      setResultsTab('table');
      
      // Set pagination metadata
      const totalRows = savedResult.rows?.length || 0;
      setTotalResultRows(totalRows);
      setTotalResultPages(Math.ceil(totalRows / resultsPageSize) || 1);
      setResultsPage(1);
      
      // Show first page
      setPaginatedRows((savedResult.rows || []).slice(0, resultsPageSize));
      
      toast.success(`Loaded saved "${entry.report_name}"`);
    } else {
      setResult(null);
      setResultRanBy(null);
      setCurrentReportRunId(null);
      setDrillDownData(null);
      setDrillDownLabel('');
      toast.info('Report settings loaded. Click "Run Report" to refresh.');
    }
    
    setHistoryOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Run again - refresh data for a history entry
  const handleRunAgain = useCallback(async (entry, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    setSelectedReportId(entry.report_key);
    const params = entry.params_json || {};
    if (params.date_from) setDateFrom(params.date_from);
    if (params.date_to) setDateTo(params.date_to);
    if (params.filters) setFilters(params.filters);
    if (params.group_by !== undefined) setGroupBy(params.group_by || '');
    
    setHistoryOpen(false);
    
    // Wait for state to update, then run
    setTimeout(() => {
      handleRunReport(entry.id, true);
    }, 100);
  }, [handleRunReport]);

  // Toggle pin on a history entry
  const handleTogglePin = useCallback(async (entry, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    try {
      const response = await reportsAPI.togglePin(entry.id);
      setReportHistory(prev => {
        const updated = prev.map(h => h.id === entry.id ? { ...h, is_pinned: response.data.is_pinned, pinned_at: response.data.pinned_at } : h);
        // Re-sort: pinned first, then by created_at
        return updated.sort((a, b) => {
          if (a.is_pinned !== b.is_pinned) return b.is_pinned ? 1 : -1;
          return new Date(b.created_at) - new Date(a.created_at);
        });
      });
      toast.success(response.data.is_pinned ? 'Report pinned' : 'Report unpinned');
    } catch (error) {
      toast.error('Failed to update pin status');
    }
  }, []);

  // Clear history
  const handleClearHistory = useCallback(async (includePinned) => {
    setClearingHistory(true);
    try {
      await reportsAPI.clearHistory(includePinned);
      if (includePinned) {
        setReportHistory([]);
      } else {
        setReportHistory(prev => prev.filter(h => h.is_pinned));
      }
      setClearDialogOpen(false);
      toast.success(includePinned ? 'All history cleared' : 'Non-pinned history cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    } finally {
      setClearingHistory(false);
    }
  }, []);

  // Handle card click (primary action)
  const handleCardClick = useCallback((entry) => {
    if (entry.result_summary_json) {
      handleLoadSaved(entry);
    } else {
      handleRunAgain(entry);
    }
  }, [handleLoadSaved, handleRunAgain]);

  const handlePrint = useCallback(() => { window.print(); }, []);

  // Drill-down: load detail for a PO-by-warehouse summary row
  const handleDrillDown = async (row) => {
    if (selectedReportId !== 'part_request_pos') return;
    setDrillDownLoading(true);
    setDrillDownLabel(`${row.warehouse_name} → ${row.department_name}`);
    try {
      const res = await reportsAPI.run({
        report_id: 'part_request_pos_detail',
        date_from: dateFrom,
        date_to: dateTo,
        filters: {
          warehouse_id: row.warehouse_id || undefined,
          department_id: row.department_id || '__unassigned__',
          receipt_status: filters.receipt_status || undefined,
          po_source: filters.po_source || undefined,
        },
      });
      setDrillDownData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load detail');
    } finally {
      setDrillDownLoading(false);
    }
  };

  const handleExportReport = async () => {
    if (!selectedReportId) {
      toast.error('Please select a report');
      return;
    }
    setExporting(true);
    try {
      const response = await reportsAPI.export({
        report_id: selectedReportId,
        date_from: dateFrom,
        date_to: dateTo,
        group_by: groupBy || null,
        filters: filters,
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedReportId}_${dateFrom}_${dateTo}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const setDatePreset = useCallback((preset) => {
    const today = new Date();
    setDateRangeType(preset === 'last7' ? 'last_7_days' : preset === 'last30' ? 'last_30_days' : preset === 'thisMonth' ? 'this_month' : preset === 'lastMonth' ? 'last_month' : 'custom');
    switch (preset) {
      case 'last7':
        setDateFrom(format(subDays(today, 7), 'yyyy-MM-dd'));
        setDateTo(format(today, 'yyyy-MM-dd'));
        break;
      case 'last30':
        setDateFrom(format(subDays(today, 30), 'yyyy-MM-dd'));
        setDateTo(format(today, 'yyyy-MM-dd'));
        break;
      case 'thisMonth':
        setDateFrom(format(startOfMonth(today), 'yyyy-MM-dd'));
        setDateTo(format(today, 'yyyy-MM-dd'));
        break;
      case 'lastMonth':
        const lastMonth = subMonths(today, 1);
        setDateFrom(format(startOfMonth(lastMonth), 'yyyy-MM-dd'));
        setDateTo(format(endOfMonth(lastMonth), 'yyyy-MM-dd'));
        break;
      default:
        break;
    }
  }, []);

  // Template handlers
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!selectedReportId) {
      toast.error('Please select a report first');
      return;
    }
    
    setSavingTemplate(true);
    try {
      const templateData = {
        name: templateName.trim(),
        description: templateDescription.trim() || null,
        report_type: selectedReportId,
        filters: filters,
        group_by: groupBy || null,
        date_range_type: dateRangeType,
        date_from: dateRangeType === 'custom' ? dateFrom : null,
        date_to: dateRangeType === 'custom' ? dateTo : null,
      };
      
      if (editingTemplate) {
        const response = await reportsAPI.updateTemplate(editingTemplate.id, templateData);
        setTemplates(prev => prev.map(t => t.id === editingTemplate.id ? response.data : t));
        toast.success(`Template "${templateName}" updated`);
      } else {
        const response = await reportsAPI.createTemplate(templateData);
        setTemplates(prev => [response.data, ...prev]);
        toast.success(`Template "${templateName}" saved`);
      }
      
      setSaveTemplateOpen(false);
      setTemplateName('');
      setTemplateDescription('');
      setEditingTemplate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleRunTemplate = useCallback(async (template) => {
    // Apply template settings
    setSelectedReportId(template.report_type);
    setFilters(template.filters || {});
    setGroupBy(template.group_by || '');
    
    // Calculate dates based on date_range_type
    const today = new Date();
    let newDateFrom, newDateTo;
    
    switch (template.date_range_type) {
      case 'last_7_days':
        newDateFrom = format(subDays(today, 7), 'yyyy-MM-dd');
        newDateTo = format(today, 'yyyy-MM-dd');
        break;
      case 'last_30_days':
        newDateFrom = format(subDays(today, 30), 'yyyy-MM-dd');
        newDateTo = format(today, 'yyyy-MM-dd');
        break;
      case 'this_month':
        newDateFrom = format(startOfMonth(today), 'yyyy-MM-dd');
        newDateTo = format(today, 'yyyy-MM-dd');
        break;
      case 'last_month':
        const lastMonth = subMonths(today, 1);
        newDateFrom = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
        newDateTo = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
        break;
      case 'custom':
        newDateFrom = template.date_from || format(subDays(today, 30), 'yyyy-MM-dd');
        newDateTo = template.date_to || format(today, 'yyyy-MM-dd');
        break;
      default:
        newDateFrom = format(subDays(today, 30), 'yyyy-MM-dd');
        newDateTo = format(today, 'yyyy-MM-dd');
    }
    
    setDateFrom(newDateFrom);
    setDateTo(newDateTo);
    setDateRangeType(template.date_range_type);
    
    toast.info(`Running "${template.name}"...`);
    
    // Run report directly with template params
    setRunning(true);
    try {
      const response = await reportsAPI.run({
        report_id: template.report_type,
        date_from: newDateFrom,
        date_to: newDateTo,
        group_by: template.group_by || null,
        filters: template.filters || {},
      });
      
      setResult(response.data);
      setResultRanBy(user?.name || '—');
      setResultsTab('table');
      
      // Save to history
      const reportObj = reports.find(r => r.id === template.report_type);
      const resultSummary = {
        columns: response.data.columns,
        rows: response.data.rows?.slice(0, 100) || [],
        totals: response.data.totals,
        generated_at: response.data.generated_at,
      };
      
      try {
        const createRes = await reportsAPI.createHistory({
          report_key: template.report_type,
          report_name: reportObj?.name || template.report_type,
          params_json: { date_from: newDateFrom, date_to: newDateTo, filters: template.filters || {}, group_by: template.group_by },
          result_summary_json: resultSummary,
          row_count: response.data.rows?.length || 0,
        });
        setReportHistory(prev => [createRes.data, ...prev].slice(0, 20));
      } catch (historyError) {
        console.warn('Failed to save report history:', historyError);
      }
      
      toast.success(`${template.name} completed`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') :
                       'Failed to run report';
      toast.error(errorMsg);
    } finally {
      setRunning(false);
    }
  }, [reports, user]);

  const handleEditTemplate = useCallback((template) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description || '');
    setSaveTemplateOpen(true);
  }, []);

  const handleDeleteTemplate = async () => {
    if (!deleteTemplateConfirm) return;
    
    setDeletingTemplate(true);
    try {
      await reportsAPI.deleteTemplate(deleteTemplateConfirm.id);
      setTemplates(prev => prev.filter(t => t.id !== deleteTemplateConfirm.id));
      toast.success(`Template "${deleteTemplateConfirm.name}" deleted`);
      setDeleteTemplateConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    } finally {
      setDeletingTemplate(false);
    }
  };

  const handleToggleTemplatePin = async (template, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    try {
      const response = await reportsAPI.updateTemplate(template.id, { is_pinned: !template.is_pinned });
      setTemplates(prev => {
        const updated = prev.map(t => t.id === template.id ? response.data : t);
        return updated.sort((a, b) => {
          if (a.is_pinned !== b.is_pinned) return b.is_pinned ? 1 : -1;
          return new Date(b.created_at) - new Date(a.created_at);
        });
      });
      toast.success(response.data.is_pinned ? 'Template pinned' : 'Template unpinned');
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const getLastRunHint = useMemo(() => {
    if (reportHistory.length === 0) return null;
    const last = reportHistory[0];
    const timeAgo = formatDistanceToNow(new Date(last.created_at), { addSuffix: true });
    return `${last.report_name} · ${timeAgo}`;
  }, [reportHistory]);
  
  const chartConfig = useMemo(() => {
    if (!result || !result.rows || result.rows.length === 0) return null;
    return getChartConfig(selectedReportId, result.columns, result.rows);
  }, [result, selectedReportId]);
  
  const chartData = useMemo(() => {
    if (!chartConfig || !result?.rows) return [];
    return result.rows.map((row, idx) => ({
      name: String(row[chartConfig.labelKey] || `Item ${idx + 1}`),
      value: Number(row[chartConfig.valueKey]) || 0,
      fill: CHART_COLORS[idx % CHART_COLORS.length]
    }));
  }, [chartConfig, result]);

  // Phase 3.5: Server-side paginated history - pinned items come first in API response
  // Split the already-paginated results for display
  const pinnedHistory = useMemo(() => reportHistory.filter(h => h.is_pinned), [reportHistory]);
  const nonPinnedHistory = useMemo(() => reportHistory.filter(h => !h.is_pinned), [reportHistory]);
  
  // Total counts from server
  const pinnedCount = pinnedHistory.length;
  const nonPinnedCount = totalHistoryItems - pinnedCount;

  // Separate pinned and non-pinned templates
  const pinnedTemplates = useMemo(() => templates.filter(t => t.is_pinned), [templates]);
  const nonPinnedTemplates = useMemo(() => templates.filter(t => !t.is_pinned), [templates]);

  // Get report name from id
  const getReportName = useCallback((reportId) => {
    const report = reports.find(r => r.id === reportId);
    return report?.name || reportId;
  }, [reports]);

  // Format date range type for display
  const formatDateRangeType = (type) => {
    const labels = {
      'last_7_days': 'Last 7 days',
      'last_30_days': 'Last 30 days',
      'this_month': 'This month',
      'last_month': 'Last month',
      'this_quarter': 'This quarter',
      'last_quarter': 'Last quarter',
      'this_year': 'This year',
      'custom': 'Custom dates'
    };
    return labels[type] || type;
  };

  // Skeleton loading state for immediate render
  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 pb-20 lg:pb-0">
          {/* Header skeleton */}
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="h-8 bg-muted animate-pulse rounded w-32 mb-2" />
              <div className="h-4 bg-muted animate-pulse rounded w-64" />
            </div>
          </div>
          
          {/* Report options card skeleton */}
          <Card>
            <CardHeader className="pb-4">
              <div className="h-6 bg-muted animate-pulse rounded w-40" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="h-12 bg-muted animate-pulse rounded" />
              <div className="grid gap-4 md:grid-cols-2">
                <div className="h-10 bg-muted animate-pulse rounded" />
                <div className="h-10 bg-muted animate-pulse rounded" />
              </div>
              <div className="h-12 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Print Styles */}
      <style>{`
        @media print {
          nav, aside, header, footer, [data-print-hide="true"], .no-print { display: none !important; }
          body { background: white !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          [data-print-show="true"] { display: block !important; }
          .print-results-card { box-shadow: none !important; border: none !important; margin: 0 !important; padding: 0 !important; }
          table { width: 100% !important; border-collapse: collapse !important; font-size: 10pt !important; }
          th, td { border: 1px solid #ddd !important; padding: 6px 8px !important; text-align: left !important; }
          th { background: #f5f5f5 !important; font-weight: bold !important; }
          tr { page-break-inside: avoid; }
          .print-chart { width: 100% !important; height: 400px !important; }
        }
      `}</style>
      
      {/* Clear History Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear Report History</DialogTitle>
            <DialogDescription>
              Choose how to clear your report history. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setClearDialogOpen(false)} disabled={clearingHistory}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={() => handleClearHistory(false)} disabled={clearingHistory || pinnedHistory.length === reportHistory.length}>
              {clearingHistory ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear non-pinned ({nonPinnedHistory.length})
            </Button>
            <Button variant="destructive" onClick={() => handleClearHistory(true)} disabled={clearingHistory}>
              {clearingHistory ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear all ({reportHistory.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Save Template Dialog */}
      <Dialog open={saveTemplateOpen} onOpenChange={(open) => {
        setSaveTemplateOpen(open);
        if (!open) {
          setTemplateName('');
          setTemplateDescription('');
          setEditingTemplate(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookTemplate className="w-5 h-5 text-primary" />
              {editingTemplate ? 'Edit Template' : 'Save as Template'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate 
                ? 'Update this template with new settings.'
                : 'Save the current report configuration as a reusable template.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input 
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Weekly PO by Technician"
                data-testid="template-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template-description">Description (optional)</Label>
              <Textarea
                id="template-description"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Brief description of what this template is for..."
                rows={2}
              />
            </div>
            
            <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
              <p className="font-medium">Configuration to save:</p>
              <p className="text-muted-foreground">• Report: {selectedReport?.name || 'None selected'}</p>
              <p className="text-muted-foreground">• Date Range: {formatDateRangeType(dateRangeType)}</p>
              {groupBy && <p className="text-muted-foreground">• Group by: {groupBy}</p>}
              {Object.keys(filters).filter(k => filters[k]).length > 0 && (
                <p className="text-muted-foreground">• Filters: {Object.keys(filters).filter(k => filters[k]).length} active</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTemplateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} disabled={savingTemplate || !templateName.trim() || !selectedReportId}>
              {savingTemplate && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingTemplate ? 'Update Template' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Confirmation */}
      <Dialog open={!!deleteTemplateConfirm} onOpenChange={(open) => !open && setDeleteTemplateConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deleteTemplateConfirm?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTemplateConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteTemplate} disabled={deletingTemplate}>
              {deletingTemplate && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Report Modal */}
      <ScheduleReportModal
        open={scheduleModalOpen}
        onOpenChange={setScheduleModalOpen}
        reportType={selectedReportId}
        reportName={selectedReport?.name || ''}
        currentFilters={filters}
        dateRangeType={dateRangeType}
        onCreated={() => toast.success('View your schedules on the Scheduled Reports page.')}
      />
      
      <div className="space-y-6 pb-20 lg:pb-0 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 no-print">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground">Reports</h1>
            <p className="text-muted-foreground mt-1 text-sm md:text-base">Run and export business reports</p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => navigate('/scheduled-reports')} data-testid="view-schedules-link">
              <CalendarClock className="w-4 h-4 mr-2" />Schedules
            </Button>
          </div>
        </div>

        {/* Export actions (shown when results exist) */}
        {hasPerm('reports.export') && result && (
          <div className="flex items-center justify-end gap-2 no-print">
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
              <Printer className="w-4 h-4 mr-2" />Print / PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportReport} disabled={exporting} data-testid="export-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
          </div>
        )}

        {/* Report Options Card */}
        <Card className="no-print">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2"><Filter className="w-5 h-5" />Report Options</CardTitle>
          </CardHeader>
          <CardContent>
            <FilterControls
              reports={reports} selectedReportId={selectedReportId} setSelectedReportId={setSelectedReportId}
              selectedReport={selectedReport} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo}
              setDateTo={setDateTo} groupBy={groupBy} setGroupBy={setGroupBy} filters={filters} setFilters={setFilters}
              vendors={vendors} technicians={technicians} statuses={statuses} items={items} vehicles={vehicles}
              actors={actors} eventTypes={eventTypes} categories={categories} locationTypes={locationTypes}
              approvalStatuses={approvalStatuses} approvalTypes={approvalTypes} approvers={approvers}
              requesters={requesters} reportWarehouses={reportWarehouses} reportDepartments={reportDepartments}
              running={running} handleRunReport={() => handleRunReport(null, false)} setDatePreset={setDatePreset}
              onSaveTemplate={() => setSaveTemplateOpen(true)}
              onSchedule={() => setScheduleModalOpen(true)}
              canSchedule={user?.role === 'owner' || user?.role === 'admin'}
              filtersLoading={filtersLoading}
            />
          </CardContent>
        </Card>

        {/* Report Results Card */}
        {result ? (
          <Card className="print-results-card">
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <CardTitle className="text-lg">{selectedReport?.name} Results</CardTitle>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs text-muted-foreground">
                    <span>Generated: {format(new Date(result.generated_at), 'MMM d, yyyy h:mm a')}</span>
                    {resultRanBy && (
                      <>
                        <span className="hidden sm:inline">·</span>
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />Ran by: {resultRanBy}</span>
                      </>
                    )}
                  </div>
                </div>
                {result.rows.length > 0 && chartConfig && (
                  <div className="no-print">
                    <Tabs value={resultsTab} onValueChange={setResultsTab}>
                      <TabsList className="h-9">
                        <TabsTrigger value="table" className="text-xs gap-1.5"><TableIcon className="w-3.5 h-3.5" />Table</TabsTrigger>
                        <TabsTrigger value="chart" className="text-xs gap-1.5"><BarChart3 className="w-3.5 h-3.5" />Chart</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {result.rows.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No data found</p>
                  <p className="text-sm mt-1">Try adjusting your date range or filters</p>
                </div>
              ) : (
                <>
                  <div className={resultsTab === 'table' ? 'block' : 'hidden'} data-print-show="true">
                    <div className="overflow-x-auto -mx-6 px-6">
                      <TooltipProvider>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {result.columns.map((col) => (
                                <TableHead key={col.key} className={`whitespace-nowrap ${col.type === 'currency' || col.type === 'number' ? 'text-right' : ''}`}>
                                  <div className="flex items-center gap-1">
                                    {col.label}
                                    {col.tooltip && (
                                      <ShadcnTooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                          <p className="text-xs">{col.tooltip}</p>
                                        </TooltipContent>
                                      </ShadcnTooltip>
                                    )}
                                  </div>
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {paginatedRows.map((row, idx) => (
                              <TableRow
                                key={idx}
                                className={selectedReportId === 'part_request_pos' ? 'cursor-pointer hover:bg-muted/60' : ''}
                                onClick={() => selectedReportId === 'part_request_pos' && handleDrillDown(row)}
                                data-testid={selectedReportId === 'part_request_pos' ? `drill-down-row-${idx}` : undefined}
                              >
                                {result.columns.map((col) => (
                                  <TableCell key={col.key} className={`whitespace-nowrap ${col.type === 'currency' || col.type === 'number' ? 'text-right font-mono' : ''}`}>
                                    {/* Show incomplete cost indicator for recorded_value column */}
                                    {col.key === 'recorded_value' && row.cost_incomplete ? (
                                      <div className="flex items-center justify-end gap-1.5">
                                        <ShadcnTooltip>
                                          <TooltipTrigger asChild>
                                            <span className="inline-flex items-center gap-1 text-amber-600">
                                              <AlertTriangle className="w-3.5 h-3.5" />
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-xs">
                                            <p className="text-xs">One or more items are missing unit cost. Value shown reflects entered costs only.</p>
                                          </TooltipContent>
                                        </ShadcnTooltip>
                                        {/* Display — instead of $0.00 when cost_incomplete and value is 0 */}
                                        <span>{row[col.key] === 0 || row[col.key] === null ? '—' : formatCellValue(row[col.key], col.type)}</span>
                                      </div>
                                    ) : (
                                      formatCellValue(row[col.key], col.type)
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        
                        {/* Phase 3.5: Server-Side Results Pagination */}
                        {totalResultRows > resultsPageSize && (
                          <ListPagination
                            page={resultsPage}
                            pageSize={resultsPageSize}
                            total={totalResultRows}
                            totalPages={totalResultPages}
                            onPageChange={handleResultsPageChange}
                            onPageSizeChange={handleResultsPageSizeChange}
                            loading={running || resultsLoading}
                            pageSizeOptions={[15, 25, 50, 100]}
                          />
                        )}
                      </TooltipProvider>
                    </div>
                  </div>
                  
                  {chartConfig && (
                    <div className={`${resultsTab === 'chart' ? 'block' : 'hidden'} no-print`}>
                      <div className="print-chart" style={{ height: '400px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" className="opacity-50" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} interval={0} />
                            <YAxis tickFormatter={(value) => chartConfig.valueColumn.type === 'currency' ? `$${value.toLocaleString()}` : value.toLocaleString()} tick={{ fontSize: 11 }} />
                            <RechartsTooltip formatter={(value) => [chartConfig.valueColumn.type === 'currency' ? `$${Number(value).toFixed(2)}` : Number(value).toLocaleString(), chartConfig.valueLabel]} labelStyle={{ fontWeight: 'bold' }} />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                              {chartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <p className="text-center text-xs text-muted-foreground mt-2">{chartConfig.chartTitle} — {chartConfig.valueLabel}</p>
                    </div>
                  )}

                  {result.totals && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex flex-wrap gap-4">
                        {Object.entries(result.totals).map(([key, value]) => {
                          const col = result.columns.find(c => c.key === key);
                          const label = col?.label || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                          return (
                            <div key={key} className="text-sm">
                              <span className="text-muted-foreground">{label}: </span>
                              <span className="font-bold">{col?.type === 'currency' ? `$${Number(value).toFixed(2)}` : Number(value).toLocaleString()}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Part Request → PO drill-down panel */}
                  {selectedReportId === 'part_request_pos' && (drillDownData || drillDownLoading) && (
                    <div className="mt-6 pt-4 border-t border-border" data-testid="drill-down-panel">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-foreground">
                          {drillDownLabel} — PO Detail
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setDrillDownData(null); setDrillDownLabel(''); }}
                          data-testid="close-drill-down"
                        >
                          <X className="w-4 h-4 mr-1" /> Close
                        </Button>
                      </div>
                      {drillDownLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                          <span className="ml-2 text-sm text-muted-foreground">Loading detail...</span>
                        </div>
                      ) : drillDownData && drillDownData.rows.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {drillDownData.columns.map((col) => (
                                <TableHead key={col.key} className={col.type === 'currency' || col.type === 'number' ? 'text-right' : ''}>
                                  {col.label}
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {drillDownData.rows.map((row, idx) => (
                              <TableRow key={idx}>
                                {drillDownData.columns.map((col) => (
                                  <TableCell key={col.key} className={col.type === 'currency' || col.type === 'number' ? 'text-right font-mono' : ''}>
                                    {col.type === 'currency' ? `$${Number(row[col.key]).toFixed(2)}` : row[col.key]}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">No POs found for this group.</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-dashed no-print">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
              <p className="text-lg font-semibold text-foreground">Select a report and run it</p>
              <p className="text-muted-foreground mt-1 text-center max-w-md">
                Choose a report from the options above, set your date range and filters, then click &quot;Run Report&quot;
              </p>
            </CardContent>
          </Card>
        )}

        {/* Saved Templates Section */}
        <Card className="no-print">
          <Collapsible open={templatesOpen} onOpenChange={setTemplatesOpen}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-2 text-left group flex-1" data-testid="templates-toggle">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <BookTemplate className="w-5 h-5" />
                      Saved Templates
                      {!templatesOpen && templates.length > 0 && (
                        <span className="text-sm font-normal text-muted-foreground ml-1">
                          ({templates.length} saved)
                        </span>
                      )}
                    </CardTitle>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${templatesOpen ? '' : '-rotate-90'}`} />
                  </button>
                </CollapsibleTrigger>
              </div>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="pt-0">
                {templatesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <BookTemplate className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No saved templates yet</p>
                    <p className="text-sm">Configure a report and click &quot;Save as Template&quot; to create one</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Pinned Templates */}
                    {pinnedTemplates.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          <Pin className="w-3 h-3" />Pinned
                        </div>
                        {pinnedTemplates.map((template) => (
                          <TemplateCard
                            key={template.id}
                            template={template}
                            getReportName={getReportName}
                            formatDateRangeType={formatDateRangeType}
                            onRun={handleRunTemplate}
                            onEdit={handleEditTemplate}
                            onDelete={setDeleteTemplateConfirm}
                            onTogglePin={handleToggleTemplatePin}
                          />
                        ))}
                      </div>
                    )}
                    
                    {/* Regular Templates */}
                    {nonPinnedTemplates.length > 0 && (
                      <div className="space-y-2">
                        {pinnedTemplates.length > 0 && (
                          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            <Bookmark className="w-3 h-3" />Templates
                          </div>
                        )}
                        {nonPinnedTemplates.map((template) => (
                          <TemplateCard
                            key={template.id}
                            template={template}
                            getReportName={getReportName}
                            formatDateRangeType={formatDateRangeType}
                            onRun={handleRunTemplate}
                            onEdit={handleEditTemplate}
                            onDelete={setDeleteTemplateConfirm}
                            onTogglePin={handleToggleTemplatePin}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Reports History Collapsible */}
        <Card className="no-print">
          <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-2 text-left group flex-1" data-testid="history-toggle">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <History className="w-5 h-5" />
                      Reports History
                      {!historyOpen && (
                        <span className="text-sm font-normal text-muted-foreground ml-1 truncate max-w-[300px]">
                          {getLastRunHint ? `(Last: ${getLastRunHint})` : '(No reports run yet)'}
                        </span>
                      )}
                    </CardTitle>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${historyOpen ? '' : '-rotate-90'}`} />
                  </button>
                </CollapsibleTrigger>
                
                {historyOpen && reportHistory.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setClearDialogOpen(true)} className="text-muted-foreground hover:text-destructive" data-testid="clear-history-btn">
                    <Trash2 className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Clear</span>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="pt-0">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : reportHistory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No reports run yet</p>
                    <p className="text-sm">Reports you run will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Pinned Section */}
                    {pinnedHistory.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          <Pin className="w-3 h-3" />Pinned
                        </div>
                        {pinnedHistory.map((entry) => (
                          <HistoryEntryCard key={entry.id} entry={entry} onCardClick={handleCardClick} onRunAgain={handleRunAgain} onTogglePin={handleTogglePin} />
                        ))}
                      </div>
                    )}
                    
                    {/* Recent Section */}
                    {nonPinnedHistory.length > 0 && (
                      <div className="space-y-2">
                        {pinnedHistory.length > 0 && (
                          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            <Clock className="w-3 h-3" />Recent
                          </div>
                        )}
                        {nonPinnedHistory.map((entry) => (
                          <HistoryEntryCard key={entry.id} entry={entry} onCardClick={handleCardClick} onRunAgain={handleRunAgain} onTogglePin={handleTogglePin} />
                        ))}
                        
                        {/* Phase 3.5: Server-Side History Pagination */}
                        {totalHistoryItems > historyPageSize && (
                          <ListPagination
                            page={historyPage}
                            pageSize={historyPageSize}
                            total={totalHistoryItems}
                            totalPages={totalHistoryPages}
                            onPageChange={handleHistoryPageChange}
                            onPageSizeChange={handleHistoryPageSizeChange}
                            loading={historyLoading}
                            pageSizeOptions={[10, 20, 30, 50]}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      </div>
    </Layout>
  );
};

// Template Card Component
const TemplateCard = ({ template, getReportName, formatDateRangeType, onRun, onEdit, onDelete, onTogglePin }) => {
  const filterCount = Object.keys(template.filters || {}).filter(k => template.filters[k]).length;
  
  return (
    <div className="p-3 border rounded-lg hover:bg-muted/30 hover:border-primary/20 transition-all duration-150 group" data-testid={`template-${template.id}`}>
      {/* Mobile Layout */}
      <div className="sm:hidden">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            {template.is_pinned && <Pin className="w-3 h-3 text-primary shrink-0" />}
            <span className="font-medium truncate">{template.name}</span>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs">
            {getReportName(template.report_type)}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs text-muted-foreground">
            {formatDateRangeType(template.date_range_type)}
          </span>
          {template.group_by && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs text-muted-foreground">
              By {template.group_by}
            </span>
          )}
          {filterCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs text-muted-foreground">
              {filterCount} filter{filterCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        {template.description && (
          <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{template.description}</p>
        )}
        
        <div className="flex items-center justify-between gap-2">
          <Button size="sm" onClick={() => onRun(template)} className="flex-1" data-testid={`run-template-${template.id}`}>
            <Play className="w-3 h-3 mr-1" />Run
          </Button>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => onTogglePin(template, e)}
              className="p-1.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-primary transition-colors"
              aria-label={template.is_pinned ? 'Unpin template' : 'Pin template'}
            >
              {template.is_pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors">
                  <MoreVertical className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(template)}>
                  <Edit className="w-4 h-4 mr-2" />Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDelete(template)} className="text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Desktop Layout */}
      <div className="hidden sm:flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
            <BookTemplate className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {template.is_pinned && <Pin className="w-3 h-3 text-primary shrink-0" />}
              <span className="font-medium truncate">{template.name}</span>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[10px] font-medium shrink-0">
                {getReportName(template.report_type)}
              </span>
            </div>
            <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
              <span>{formatDateRangeType(template.date_range_type)}</span>
              {template.group_by && (
                <>
                  <span>·</span>
                  <span>By {template.group_by}</span>
                </>
              )}
              {filterCount > 0 && (
                <>
                  <span>·</span>
                  <span>{filterCount} filter{filterCount !== 1 ? 's' : ''}</span>
                </>
              )}
              {template.description && (
                <>
                  <span>·</span>
                  <span className="truncate max-w-[200px]" title={template.description}>{template.description}</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground hidden lg:block">
            By {template.created_by_name}
          </span>
          
          <Button size="sm" onClick={() => onRun(template)} data-testid={`run-template-${template.id}`}>
            <Play className="w-3 h-3 mr-1" />Run
          </Button>
          
          <button
            onClick={(e) => onTogglePin(template, e)}
            className="p-1.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-primary transition-colors"
            aria-label={template.is_pinned ? 'Unpin template' : 'Pin template'}
          >
            {template.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
          </button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors">
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(template)}>
                <Edit className="w-4 h-4 mr-2" />Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDelete(template)} className="text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

// History Entry Card Component
const HistoryEntryCard = ({ entry, onCardClick, onRunAgain, onTogglePin }) => {
  const hasSavedResults = !!entry.result_summary_json;
  const params = entry.params_json || {};
  const timeAgo = formatDistanceToNow(new Date(entry.created_at), { addSuffix: true });
  
  const handleCardClick = (e) => {
    // Prevent triggering if clicking on action buttons
    if (e.target.closest('[data-action-button]')) return;
    onCardClick(entry);
  };
  
  return (
    <div
      onClick={handleCardClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(e); } }}
      role="button"
      tabIndex={0}
      className="w-full text-left p-3 border rounded-lg hover:bg-muted/50 hover:border-primary/30 active:bg-muted/70 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-all duration-150 cursor-pointer group"
      data-testid={`history-entry-${entry.id}`}
      aria-label={`${hasSavedResults ? 'Load saved results for' : 'Run'} report: ${entry.report_name}`}
    >
      {/* Mobile Layout */}
      <div className="sm:hidden">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            {entry.is_pinned && <Pin className="w-3 h-3 text-primary shrink-0" />}
            <span className="font-medium truncate group-hover:text-primary transition-colors">{entry.report_name}</span>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1">
            <Clock className="w-3 h-3" />{timeAgo}
          </span>
        </div>
        
        <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
          {hasSavedResults && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-medium">
              <Archive className="w-2.5 h-2.5 mr-0.5" />Saved
            </span>
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs text-muted-foreground">
            {params.date_from ? format(new Date(params.date_from), 'MMM d') : '—'} – {params.date_to ? format(new Date(params.date_to), 'MMM d') : '—'}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs text-muted-foreground">
            {entry.row_count} {entry.row_count === 1 ? 'row' : 'rows'}
          </span>
        </div>
        
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground truncate">Ran by {entry.ran_by_label}</span>
          <div className="flex items-center gap-1">
            <button
              data-action-button
              onClick={(e) => onTogglePin(entry, e)}
              className="p-1 rounded hover:bg-muted/80 text-muted-foreground hover:text-primary transition-colors"
              aria-label={entry.is_pinned ? 'Unpin report' : 'Pin report'}
            >
              {entry.is_pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
            </button>
            <button
              data-action-button
              onClick={(e) => onRunAgain(entry, e)}
              className="p-1 rounded hover:bg-muted/80 text-muted-foreground hover:text-primary transition-colors"
              aria-label="Run again"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Desktop Layout */}
      <div className="hidden sm:flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${hasSavedResults ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-primary/10 text-primary'} group-hover:bg-primary/20`}>
            {hasSavedResults ? <Archive className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {entry.is_pinned && <Pin className="w-3 h-3 text-primary shrink-0" />}
              <span className="font-medium truncate group-hover:text-primary transition-colors">{entry.report_name}</span>
              {hasSavedResults && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-medium shrink-0">
                  Saved
                </span>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {params.date_from ? format(new Date(params.date_from), 'MMM d') : '—'} – {params.date_to ? format(new Date(params.date_to), 'MMM d, yyyy') : '—'}
              <span className="mx-1">·</span>
              {entry.row_count} {entry.row_count === 1 ? 'row' : 'rows'}
              <span className="mx-1">·</span>
              <span className="inline-flex items-center gap-0.5"><User className="w-3 h-3" />Ran by {entry.ran_by_label}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex flex-col items-end gap-1">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />{timeAgo}
            </span>
            <span className="text-xs text-primary opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
              {hasSavedResults ? 'Click to load saved' : 'Click to run'}
            </span>
          </div>
          
          <div className="flex items-center gap-1 ml-2">
            <button
              data-action-button
              onClick={(e) => onTogglePin(entry, e)}
              className="p-1.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-primary transition-colors"
              aria-label={entry.is_pinned ? 'Unpin report' : 'Pin report'}
            >
              {entry.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
            </button>
            <button
              data-action-button
              onClick={(e) => onRunAgain(entry, e)}
              className="p-1.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-primary transition-colors"
              aria-label="Run again (refresh data)"
              title="Run again (refresh data)"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
